// src/helpers/permissions.js
import { jwtDecode } from "jwt-decode";
import axios from 'axios';
import { API_URL } from '../config';

let cachedRoleDetails = null;
let cachedUserId = null;
let isInitialized = false;

/**
 * Fetches role details from API and caches them
 * @returns {Promise<Object>} Parsed role details or empty object if invalid
 */
async function fetchRoleDetails() {
  try {
    const token = localStorage.getItem("authToken");
    if (!token) return {};

    const response = await axios.get(`${API_URL}auth/role-detail`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    console.log("Responess", response)

    if (response.data?.success && response.data?.data?.user_role_details) {
      cachedRoleDetails = typeof response.data.data.user_role_details === 'string'
        ? JSON.parse(response.data.data.user_role_details)
        : response.data.data.user_role_details;
      cachedUserId = response.data.data.id;
      isInitialized = true;
      return cachedRoleDetails;
    }
    return {};
  } catch (err) {
    console.error("❌ Failed to fetch role details:", err);
    return {};
  }
}

/**
 * Gets role details (uses cached version if available)
 * @returns {Object} Parsed role details or empty object if invalid
 */
export function getRoleDetails() {
  // If we have cached data, return it immediately
  if (cachedRoleDetails) return cachedRoleDetails;

  // Fallback to JWT token if API not called yet
  const token = localStorage.getItem("authToken");
  if (!token) return {};

  try {
    const decoded = jwtDecode(token);
    const rawRoleDetails = decoded?.user_roledetails || {};
    return typeof rawRoleDetails === "string"
      ? JSON.parse(rawRoleDetails)
      : rawRoleDetails;
  } catch (err) {
    console.error("❌ Failed to decode or parse role details from token:", err);
    return {};
  }
}

export function getRoleUserid() {
  // If we have cached user ID, return it
  if (cachedUserId) return cachedUserId;

  // Fallback to JWT token if API not called yet
  const token = localStorage.getItem("authToken");
  if (!token) return {};

  try {
    const decoded = jwtDecode(token);
    const userid = decoded?.userid || {};
    return typeof userid === "string" ? JSON.parse(userid) : userid;
  } catch (err) {
    console.error("❌ Failed to decode or parse user ID from token:", err);
    return {};
  }
}

/**
 * Initializes permissions by fetching from API
 * Call this when the app loads or after login
 */
// export async function initializePermissions() {
//   if (!isInitialized) {
//     await fetchRoleDetails();
//   }
// }
export async function initializePermissions(force = false) {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const currentUserId = user?.id ?? user?.tu_id ?? null;

    // return cached if already initialized for same user and not forcing refresh
    if (!force && isInitialized && cachedUserId === currentUserId && cachedRoleDetails) {
      return cachedRoleDetails;
    }

    // update cachedUserId immediately so concurrent calls won't misbehave
    cachedUserId = currentUserId;

    // call existing fetchRoleDetails (which should use authToken)
    const roleDetails = await fetchRoleDetails();

    // normalize and cache
    cachedRoleDetails = roleDetails || {};
    isInitialized = true;

    // notify app that permissions changed so components can react if they listen
    try {
      window.dispatchEvent(new Event("permissionsUpdated"));
    } catch (e) {
      // ignore in non-browser or older browsers
    }

    return cachedRoleDetails;
  } catch (err) {
    console.error("initializePermissions error:", err);
    // ensure we mark initialized to avoid infinite retries
    isInitialized = true;
    cachedRoleDetails = cachedRoleDetails || {};
    try { window.dispatchEvent(new Event("permissionsUpdated")); } catch { }
    return cachedRoleDetails;
  }
}
export function getCachedPermissions() {
  return cachedRoleDetails || {};
}
export function isPermissionsInitialized() {
  return isInitialized;
}

/**
 * Checks if the user has a specific permission for a module and field
 * @param {string} moduleKey - The module key with optional field path (e.g. 'user.name.view' or 'hrms.view')
 * @param {string} action - The action (e.g., 'view', 'edit')
 * @returns {boolean} Whether the permission is granted
 */
export function hasPermission(moduleKey, action) {
  const roleDetails = getRoleDetails();
  const roleUserid = getRoleUserid();

  // Super admin has all permissions
  if (roleUserid == "1") {
    return true;
  }

  // Handle nested permissions using dot notation
  const parts = moduleKey.split('.');
  let currentLevel = roleDetails;

  // Navigate through the permission object based on path
  for (const part of parts) {
    if (!currentLevel || typeof currentLevel !== 'object') {
      if (moduleKey === 'subscriber_portfolio') {
        return hasPermission('subscriber', action);
      }
      return false;
    }
    currentLevel = currentLevel[part];
  }

  // If we've reached a permission object, check the action
  if (!currentLevel || typeof currentLevel !== 'object') {
    if (moduleKey === 'subscriber_portfolio') {
      return hasPermission('subscriber', action);
    }
    return false;
  }

  const actionKey = action?.toLowerCase();
  if (currentLevel[actionKey] === undefined && moduleKey === 'subscriber_portfolio') {
    return hasPermission('subscriber', action);
  }
  return currentLevel[actionKey] === "1";
}

/**
 * Checks if a specific field has view permission
 * @param {string} modulePath - Full path to the field (e.g. 'user.personal.name')
 * @returns {boolean} Whether the field has view permission
 */
export function hasFieldPermission(modulePath) {
  return hasPermission(modulePath, 'view');
}

/**
 * Checks if the user has any of the given permissions for a module.
 * @param {string} moduleKey - The module key.
 * @param {string[]} actions - Array of actions (e.g., ['view', 'edit']).
 * @returns {boolean} Whether any of the permissions are granted.
 */
export function hasAnyPermission(moduleKey, actions = []) {
  const roleDetails = getRoleDetails();
  const roleUserid = getRoleUserid();

  // Super admin has all permissions
  if (roleUserid == "1") {
    return true;
  }

  let modulePermissions = roleDetails?.[moduleKey];
  if ((!modulePermissions || typeof modulePermissions !== "object") && moduleKey === 'subscriber_portfolio') {
    modulePermissions = roleDetails?.['subscriber'];
  }

  if (!modulePermissions || typeof modulePermissions !== "object") {
    return false;
  }

  return actions.some(
    action => modulePermissions?.[action?.toLowerCase()] === "1"
  );
}

export const PermissionWrapper = ({ module, action, children }) =>
  hasPermission(module, action) ? children : null;

// Automatically initialize permissions when the module is imported
// This ensures the API call happens as soon as possible
initializePermissions();
